import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { WsEvent } from '@voice-assistant/shared';
import type { ChatMessage } from '../hooks/useSocket';

// ─── State types ──────────────────────────────────────────────────────────────

/**
 * FSM mode for a single session's message-load state.
 *
 * Transitions (Phase 1):
 *   idle → fetching_initial  (on openSession(sid) miss, or startFetchingInitial at connect)
 *   fetching_initial → loaded  (on onMessagesLoaded reply)
 *   fetching_initial → error  (on fetch failure / silent server reject / fetch-timer timeout)
 *   loaded → fetching_older   (on beginLoadOlder)
 *   fetching_older → loaded   (on onOlderLoaded reply)
 *   fetching_older → error    (on fetch failure)
 *   error → fetching_initial  (on retry)
 *
 * SESSIONS_RECONCILE is the AUTHORITY for the entire per-session state shape
 * (Phase 2): on connect / reconnect the reconcile event payload tells us
 * what messages the server has for which sessions; we reconcile against it
 * rather than guessing. See plans/session-load-fsm-migration.md.
 */
export type SessionLoadMode =
  | 'idle'
  | 'fetching_initial'
  | 'loaded'
  | 'fetching_older'
  | 'error';

/**
 * R-A1: the FSM transition table, exported as data. Every row is a real
 * transition implemented below; every `trigger` names the store action (or the
 * internal fetch-timer) that performs it. The session-load SE suite
 * (setpoint-evals/session-load/SE-04-fsm-coverage) cross-checks each distinct
 * trigger against `// SE-COVERS:` markers in sessionLoadStore.spec.ts —
 * adding a row here without covering behavior fails the suite.
 */
export const TRANSITIONS: Array<{ from: SessionLoadMode; to: SessionLoadMode; trigger: string }> = [
  { from: 'idle', to: 'fetching_initial', trigger: 'openSession' },           // cache miss → fetch
  { from: 'idle', to: 'loaded', trigger: 'openSession' },                     // cache hit → no fetch
  { from: 'idle', to: 'fetching_initial', trigger: 'startFetchingInitial' },  // connect-time boot loop / applyReconcile
  { from: 'error', to: 'fetching_initial', trigger: 'startFetchingInitial' }, // reconcile retry
  { from: 'error', to: 'fetching_initial', trigger: 'retrySession' },         // user-tapped Retry
  // onMessagesLoaded / onSessionSwitched are an UNCONDITIONAL collapse to
  // `loaded` from whatever mode the session was in (both handlers write
  // `mode: 'loaded'` over `existing ?? default`, no guard). The reply can land
  // in any prior mode, so every reachable `from` is enumerated (R-A1 = honest
  // table): fetching_initial (the normal open path), loaded (a reconnect
  // re-fetch — exactly the mergeSnapshotWithLiveTail target), error (a late
  // reply after the 10 s fetchTimeout already flipped it), fetching_older (a
  // full snapshot racing an in-flight older-page fetch), and idle (a reply for
  // a session with no store entry yet — the `?? defaultSessionLoadState()`
  // branch). Same set for both reply triggers.
  { from: 'idle', to: 'loaded', trigger: 'onMessagesLoaded' },                // LOAD_TAB_MESSAGES reply, untracked session
  { from: 'fetching_initial', to: 'loaded', trigger: 'onMessagesLoaded' },    // LOAD_TAB_MESSAGES reply (normal)
  { from: 'loaded', to: 'loaded', trigger: 'onMessagesLoaded' },              // reconnect re-fetch snapshot (merge)
  { from: 'error', to: 'loaded', trigger: 'onMessagesLoaded' },               // late reply after fetchTimeout
  { from: 'fetching_older', to: 'loaded', trigger: 'onMessagesLoaded' },      // snapshot racing an older-page fetch
  { from: 'idle', to: 'loaded', trigger: 'onSessionSwitched' },               // SWITCH_SESSION reply, untracked session
  { from: 'fetching_initial', to: 'loaded', trigger: 'onSessionSwitched' },   // SWITCH_SESSION reply (normal)
  { from: 'loaded', to: 'loaded', trigger: 'onSessionSwitched' },             // reconnect re-switch snapshot (merge)
  { from: 'error', to: 'loaded', trigger: 'onSessionSwitched' },              // late reply after fetchTimeout
  { from: 'fetching_older', to: 'loaded', trigger: 'onSessionSwitched' },     // snapshot racing an older-page fetch
  { from: 'fetching_initial', to: 'error', trigger: 'fetchTimeout' },         // 10 s no-reply safety net
  { from: 'loaded', to: 'fetching_older', trigger: 'beginLoadOlder' },        // scroll-up pagination
  { from: 'fetching_older', to: 'loaded', trigger: 'onOlderLoaded' },         // OLDER_MESSAGES_LOADED reply
];

export interface SessionLoadState {
  messages: ChatMessage[];
  hasMore: boolean;
  mode: SessionLoadMode;
  /** Cursor for pagination — typically the oldest message's `id` (DB row id). */
  cursor?: string;
  /** Last error message when mode === 'error'. Cleared on next successful fetch. */
  lastError?: string;
  /** Timestamp of last successful onMessagesLoaded, for staleness checks. */
  lastLoadedAt?: number;
}

export interface SessionLoadStoreShape {
  sessions: Map<string, SessionLoadState>;
}

// ─── Default state factory ────────────────────────────────────────────────────

/**
 * Returns the default per-session state. New sessions start optimistic:
 * `hasMore: true` so the scroll-up affordance exists during the initial
 * fetch window — the authoritative MESSAGES_LOADED reply flips it to
 * `false` for genuinely empty sessions. Same rationale as the PR #1236
 * hotfix on the legacy tabCache.hasMore default.
 */
function defaultSessionLoadState(): SessionLoadState {
  return {
    messages: [],
    hasMore: true,
    mode: 'idle',
  };
}

// ─── Wire interface ────────────────────────────────────────────────────────────

/**
 * Dependency injection bridge: connects the store to socket + React state setters.
 * Injected by useSocket.ts via initSessionLoadWire().
 * Breaking this into a wire object prevents a circular import between the store
 * and the hook (store would need to import React hook, hook would import store).
 *
 * ALL callbacks must close over stable refs so reinitializing on each render
 * is idempotent.
 */
export interface SLSWire {
  /**
   * Emit a socket event to the backend (noop when socket is not yet connected).
   * MUST be a `WsEvent` enum member, NOT a bare string — the backend
   * `@SubscribeMessage` decorators match on the enum VALUE (e.g. `'switch_session'`),
   * so passing the enum KEY string `'SWITCH_SESSION'` silently never reaches the
   * server and the load times out. Typing this as `WsEvent` makes tsc enforce it.
   */
  socketEmit: (event: WsEvent, data: unknown) => void;
  /** Update activeTabIdRef + persist to localStorage + call setCurrentSessionId. */
  setActiveTab: (sid: string) => void;
  /** True iff the legacy tabCache for sid has at least one message. */
  hasCachedMessages: (sid: string) => boolean;
  /** Load legacy tabCache for sid into React state (calls existing syncTabToState). */
  syncLegacyTabToState: (sid: string) => void;
}

let _wire: SLSWire | null = null;

/**
 * Called from useSocket.ts after stable callbacks are defined.
 * All wire callbacks must close over stable refs — safe to reinitialize
 * on every render (idempotent: same function references each time).
 */
export function initSessionLoadWire(wire: SLSWire): void {
  _wire = wire;
}

// ─── Store ────────────────────────────────────────────────────────────────────

/**
 * Origin-keyed per-session message-load store.
 *
 * Phase 1 adds wire injection + actions (openSession, onMessagesLoaded, etc.)
 * for dual-write alongside the legacy tabCache. The legacy tabCache remains the
 * source of truth for rendered messages until Phase 4 deletes it.
 *
 * Persistence: NOT enabled — message arrays are large and ephemeral.
 * SESSIONS_RECONCILE will re-seed on every connect once Phase 2 lands.
 */
export const useSessionLoadStore = create<SessionLoadStoreShape>(() => ({
  sessions: new Map<string, SessionLoadState>(),
}));

// ─── Phase 1 actions ──────────────────────────────────────────────────────────

/**
 * Atomic "switch to this session" intent.
 * This is the SINGLE entry point for any open-session operation after Phase 1.
 * switchTab() in useSocket.ts becomes a thin shim that calls this.
 *
 * Transition table:
 *   any + hasCachedMessages → loaded   (local hit; sync legacy, no emit)
 *   any + !hasCachedMessages → fetching_initial + emit SWITCH_SESSION  (miss; fetch)
 *
 * The fetch-decision uses the legacy tabCache (hasCachedMessages) as the
 * source of truth during Phase 1 dual-write — the new store mode tracks UI
 * state but does not yet drive the fetch decision.
 *
 * Stuck-skeleton guard: if no MESSAGES_LOADED reply arrives within
 * FETCH_TIMEOUT_MS (10 s) the session transitions fetching_initial → error
 * with a generic "couldn't load" message. Catches the session-limit-reached
 * case where the backend rejects the open silently (toast is shown elsewhere)
 * and the frontend would otherwise spin forever.
 */
const FETCH_TIMEOUT_MS = 10_000;
const fetchTimers = new Map<string, ReturnType<typeof setTimeout>>();

function clearFetchTimer(sid: string): void {
  const t = fetchTimers.get(sid);
  if (t) { clearTimeout(t); fetchTimers.delete(sid); }
}

function armFetchTimer(sid: string): void {
  clearFetchTimer(sid);
  const t = setTimeout(() => {
    fetchTimers.delete(sid);
    useSessionLoadStore.setState((s) => {
      const existing = s.sessions.get(sid);
      if (!existing || existing.mode !== 'fetching_initial') return s;
      const sessions = new Map(s.sessions);
      sessions.set(sid, {
        ...existing,
        mode: 'error',
        lastError: 'Could not load messages — session may be unavailable or limit reached.',
      });
      return { sessions };
    });
  }, FETCH_TIMEOUT_MS);
  fetchTimers.set(sid, t);
}

export function openSession(sid: string): void {
  const wire = _wire;
  if (!wire) return;

  // Always update active tab first — prevents content bleed from the old session.
  wire.setActiveTab(sid);
  wire.syncLegacyTabToState(sid);

  if (wire.hasCachedMessages(sid)) {
    // Local hit — messages already in legacy cache; mark as loaded.
    clearFetchTimer(sid);
    useSessionLoadStore.setState((s) => {
      const sessions = new Map(s.sessions);
      const existing = sessions.get(sid) ?? defaultSessionLoadState();
      sessions.set(sid, { ...existing, mode: 'loaded' });
      return { sessions };
    });
    return;
  }

  // Miss — set fetching_initial and request from server.
  useSessionLoadStore.setState((s) => {
    const sessions = new Map(s.sessions);
    const existing = sessions.get(sid) ?? defaultSessionLoadState();
    sessions.set(sid, { ...existing, mode: 'fetching_initial' });
    return { sessions };
  });
  armFetchTimer(sid);
  wire.socketEmit(WsEvent.SWITCH_SESSION, { sessionId: sid });
}

/**
 * Retry a previously-failed session load.
 * Wired to the error-state retry button in ChatArea.
 */
export function retrySession(sid: string): void {
  const wire = _wire;
  if (!wire) return;
  useSessionLoadStore.setState((s) => {
    const existing = s.sessions.get(sid);
    if (!existing) return s;
    const sessions = new Map(s.sessions);
    sessions.set(sid, { ...existing, mode: 'fetching_initial', lastError: undefined });
    return { sessions };
  });
  armFetchTimer(sid);
  wire.socketEmit(WsEvent.SWITCH_SESSION, { sessionId: sid });
}

/**
 * Mark a session as fetching_initial at connect time, before LOAD_TAB_MESSAGES
 * response arrives. Only transitions from idle / error — does not clobber an
 * already-fetching or loaded session (reconnect idempotency).
 *
 * Stuck-skeleton safety net (#1292): arms the SAME 10 s fetch-timer openSession()
 * uses. The connect-time (useSocket.ts boot loop) and applyReconcile paths emit
 * LOAD_TAB_MESSAGES but previously had NO timeout — if the server reply was
 * silently dropped (the handleConnection auth race where the backend `!state`
 * guard returns nothing, a `!session` early-return, or a reply lost across a
 * reconnect) the session stayed in `fetching_initial` forever → infinite loading
 * skeleton in ChatArea (ChatArea.tsx renders the skeleton whenever
 * loadMode === 'fetching_initial'). The timer degrades a non-reply to `error` +
 * Retry instead. It is CAUSE-AGNOSTIC: it fires regardless of WHY the reply never
 * came, which is the correct shape for a defensive safety net. Cleared by
 * onMessagesLoaded / onSessionSwitched on the success path. Every caller emits
 * LOAD_TAB_MESSAGES immediately after this call, so the armed timer is always
 * paired with an in-flight fetch.
 */
export function startFetchingInitial(sid: string): void {
  const existing = useSessionLoadStore.getState().sessions.get(sid);
  const willFetch = !existing || existing.mode === 'idle' || existing.mode === 'error';
  if (!willFetch) return; // reconnect idempotency: don't clobber loaded / fetching_older
  useSessionLoadStore.setState((s) => {
    const sessions = new Map(s.sessions);
    const cur = sessions.get(sid) ?? defaultSessionLoadState();
    sessions.set(sid, { ...cur, mode: 'fetching_initial' });
    return { sessions };
  });
  armFetchTimer(sid);
}

/**
 * Server-authoritative reconciliation of a fetched DB snapshot against the
 * locally-rendered list — the deterministic replacement for the old
 * blind-replace (`prev => msgs`).
 *
 * THE RACE THIS KILLS (DIFFICULTIES-LOG 2026-05-19 ToolTrace-pill /
 * SYSTEM_NOTE-pill entries): a LOAD_TAB_MESSAGES / SWITCH_SESSION fetch is in
 * flight (its DB snapshot was taken at time T) while live socket events
 * (SYSTEM_NOTE, system pills, a user send in a fresh session) append messages
 * locally after T. When the reply lands, blind-replace discarded those live
 * appends — and because most of them are emitted exactly once (SYSTEM_NOTE is
 * not replayed once lastEventId advances), they were gone until the next full
 * DB fetch, or forever.
 *
 * Merge rule (deterministic, snapshot-authoritative):
 *   1. The snapshot is the base — server order and content win for everything
 *      it covers.
 *   2. Decide, per `prev` message, whether the snapshot already COVERS it. A
 *      message with a DB `id` is covered iff that id is in the snapshot (its id
 *      is its identity — a same-text row with a DIFFERENT id is a distinct row
 *      and is NOT covered). A message with NO id (a live append not yet
 *      persisted: SYSTEM_NOTE, agent_question, a fresh user send) is covered iff
 *      the snapshot still has an UNCLAIMED row of the same content identity
 *      (`identityKey`). Content claims are matched by MULTIPLICITY, earliest
 *      prev occurrence first — so a templated abort note that fired in BOTH
 *      turn 1 (persisted, in the snapshot) and turn 2 (live, no id) leaves
 *      turn 2's byte-identical copy uncovered instead of collapsing them by
 *      set membership (the #1796 byte-identical-SYSTEM_NOTE dedup). agent_question
 *      identity folds the question payload (see `identityKey`) so a racing NEW
 *      question is never swallowed by a prior one (the #1796 agent_question
 *      collision — every persisted agent_question row decodes to text:'').
 *   3. The live tail = the maximal trailing run of UNCOVERED prev messages —
 *      exactly the live-append window that postdates the snapshot. Append it
 *      after the snapshot; both sides render.
 *   4. Streaming placeholders are EXCLUDED from the preserved tail: the
 *      MESSAGES_LOADED partialText path reconstructs in-flight streams from
 *      server truth, and the chunk-flush rAF re-materialises bufferedText —
 *      resurrecting a stale placeholder here would double the bubble.
 *
 * Exported for the unit spec + setpoint-evals/session-load/.
 */
/**
 * Stable per-message identity for merge content-matching (the no-id fallback
 * when neither side carries a DB `id`). Keyed on role + text EXCEPT for
 * agent_question rows: `decodeLoadedMessage` persists their payload as
 * `questions` and sets `text:''`, so keying on role+text alone collapses EVERY
 * agent_question to one identity — a racing NEW agent_question then matches a
 * prior persisted one and is swallowed (#1796). Fold the question payload into
 * the key so distinct questions are distinct identities. The NUL separator
 * keeps the role/text (or payload) boundary unambiguous.
 */
function identityKey(m: ChatMessage): string {
  if (m.role === 'agent_question' && m.questions && m.questions.length > 0) {
    return `agent_question\u0000${JSON.stringify(m.questions)}`;
  }
  return `${m.role}\u0000${m.text}`;
}

export function mergeSnapshotWithLiveTail(
  snapshot: ChatMessage[],
  prev: ChatMessage[],
): ChatMessage[] {
  if (prev.length === 0) return snapshot;

  const snapIds = new Set<string>();
  const snapKeyById = new Map<string, string>();
  const availByKey = new Map<string, number>(); // unclaimed snapshot rows per content key
  for (const m of snapshot) {
    const key = identityKey(m);
    if (m.id) {
      snapIds.add(m.id);
      snapKeyById.set(m.id, key);
    }
    availByKey.set(key, (availByKey.get(key) ?? 0) + 1);
  }

  const covered = new Array<boolean>(prev.length).fill(false);
  // Id-claims first: a prev row whose id is in the snapshot IS that snapshot row
  // — it also consumes that row's content slot so a no-id live duplicate cannot
  // later claim the same slot (the byte-identical-note fix).
  for (let i = 0; i < prev.length; i++) {
    const m = prev[i];
    if (m.id && snapIds.has(m.id)) {
      covered[i] = true;
      const key = snapKeyById.get(m.id);
      if (key !== undefined) availByKey.set(key, (availByKey.get(key) ?? 0) - 1);
    }
  }
  // Content-claims for no-id prev rows, EARLIEST first so the LATEST duplicates
  // stay uncovered (they are the genuine live appends). A prev row that has an id
  // NOT in the snapshot is a distinct row — never content-matched.
  for (let i = 0; i < prev.length; i++) {
    if (covered[i] || prev[i].id) continue;
    const key = identityKey(prev[i]);
    const avail = availByKey.get(key) ?? 0;
    if (avail > 0) {
      covered[i] = true;
      availByKey.set(key, avail - 1);
    }
  }

  // The live tail is the maximal trailing run of UNCOVERED prev messages.
  let cut = prev.length;
  while (cut > 0 && !covered[cut - 1]) cut--;
  const tail = prev.slice(cut).filter((m) => !m.streaming);
  if (tail.length === 0) return snapshot;

  // Keep a snapshot's trailing streaming reconstruction (the isSessionBusy
  // partialText resume bubble, always pushed LAST in the MESSAGES_LOADED handler)
  // at the LAST position — splice the live tail in BEFORE it. If the tail
  // displaced the placeholder from `messages[last]`, the chunk-flush rAF and the
  // CLAUDE_DONE finaliser — which grow/finalise "the last message iff it is
  // streaming" — would no longer see it as last and would APPEND a SECOND
  // streaming bubble instead of growing the existing one (#1796 duplicate
  // streaming bubble). At most one streaming bubble renders.
  const lastSnap = snapshot[snapshot.length - 1];
  if (lastSnap?.streaming) {
    return [...snapshot.slice(0, -1), ...tail, lastSnap];
  }
  return [...snapshot, ...tail];
}

/**
 * Reply to a LOAD_TAB_MESSAGES fetch (MESSAGES_LOADED socket event).
 * Reconciles the DB snapshot against the current list via
 * mergeSnapshotWithLiveTail (racing live appends are preserved, server wins
 * for everything it covers) and transitions mode to 'loaded'.
 *
 * The store is canonical (Phase 5); callers mirror the merged result into the
 * legacy tabCache via getSessionMessages(getState(), sid) AFTER this call.
 */
export function onMessagesLoaded(
  sid: string,
  messages: ChatMessage[],
  hasMore: boolean,
): void {
  clearFetchTimer(sid);
  useSessionLoadStore.setState((s) => {
    const sessions = new Map(s.sessions);
    const existing = sessions.get(sid) ?? defaultSessionLoadState();
    const merged = mergeSnapshotWithLiveTail(messages, existing.messages);
    const cursor = messages.length > 0 ? messages[0].id : existing.cursor;
    sessions.set(sid, {
      ...existing,
      messages: merged,
      hasMore,
      mode: 'loaded',
      cursor,
      lastError: undefined,
      lastLoadedAt: Date.now(),
    });
    return { sessions };
  });
}

/**
 * Phase 5: Single chokepoint for any external mutation of the per-session
 * `messages` array. Wired into the legacy useSocket setTabMessages /
 * scheduleChunkFlush / flushChunksNow / SESSION_SWITCHED paths so the store
 * is the canonical source of truth for ChatArea (which subscribes via
 * useSessionMessages).
 *
 * Upserts: creates the entry if missing (necessary for SESSION_SWITCHED
 * + streaming chunks that may arrive before MESSAGES_LOADED has seeded).
 */
export function setSessionMessages(sid: string, messages: ChatMessage[]): void {
  useSessionLoadStore.setState((s) => {
    const sessions = new Map(s.sessions);
    const existing = sessions.get(sid) ?? defaultSessionLoadState();
    sessions.set(sid, { ...existing, messages });
    return { sessions };
  });
}

/**
 * Successful reply to a SWITCH_SESSION emit (SESSION_SWITCHED event).
 *
 * `openSession()` emits SWITCH_SESSION and the backend replies with
 * SESSION_SWITCHED (not MESSAGES_LOADED). Without this call, the fetch-timer
 * armed by openSession() never gets cleared, and 10 s later silently flips
 * the session to `error` mode — invisible while messages render (because
 * messages.length > 0 short-circuits the error UI in ChatArea) but stale
 * and incorrect; visibly broken on retry, because the user's Retry action
 * lands in the same trap and ChatArea's empty-state + error branch hits a
 * race with the slsSetMessages dispatch from the SESSION_SWITCHED handler.
 *
 * This is the SESSION_SWITCHED-path equivalent of onMessagesLoaded() and
 * applies the SAME mergeSnapshotWithLiveTail reconciliation — SESSION_SWITCHED
 * is the boot / deep-link / reconnect load path, which on mobile PWA resume is
 * exactly where a mid-turn snapshot races live SYSTEM_NOTE / pill appends.
 */
export function onSessionSwitched(
  sid: string,
  messages: ChatMessage[],
  hasMore: boolean,
): void {
  clearFetchTimer(sid);
  useSessionLoadStore.setState((s) => {
    const sessions = new Map(s.sessions);
    const existing = sessions.get(sid) ?? defaultSessionLoadState();
    const merged = mergeSnapshotWithLiveTail(messages, existing.messages);
    const cursor = messages.length > 0 ? messages[0].id : existing.cursor;
    sessions.set(sid, {
      ...existing,
      messages: merged,
      hasMore,
      mode: 'loaded',
      cursor,
      lastError: undefined,
      lastLoadedAt: Date.now(),
    });
    return { sessions };
  });
}

export function setSessionHasMore(sid: string, hasMore: boolean): void {
  useSessionLoadStore.setState((s) => {
    const sessions = new Map(s.sessions);
    const existing = sessions.get(sid) ?? defaultSessionLoadState();
    sessions.set(sid, { ...existing, hasMore });
    return { sessions };
  });
}

/**
 * Dual-write: mark session as fetching_older (cursor pagination started).
 * Only valid from 'loaded' — guards against spurious calls.
 */
export function beginLoadOlder(sid: string): void {
  useSessionLoadStore.setState((s) => {
    const sessions = new Map(s.sessions);
    const existing = sessions.get(sid);
    if (!existing || existing.mode !== 'loaded') return s;
    sessions.set(sid, { ...existing, mode: 'fetching_older' });
    return { sessions };
  });
}

/**
 * Dual-write from the OLDER_MESSAGES_LOADED socket event.
 * Prepends older messages and transitions back to 'loaded'.
 */
export function onOlderLoaded(
  sid: string,
  olderMsgs: ChatMessage[],
  hasMore: boolean,
): void {
  useSessionLoadStore.setState((s) => {
    const sessions = new Map(s.sessions);
    const existing = sessions.get(sid) ?? defaultSessionLoadState();
    const allMessages = [...olderMsgs, ...existing.messages];
    const cursor = olderMsgs.length > 0 ? olderMsgs[0].id : existing.cursor;
    sessions.set(sid, {
      ...existing,
      messages: allMessages,
      hasMore,
      mode: 'loaded',
      cursor,
      lastLoadedAt: Date.now(),
    });
    return { sessions };
  });
}

/**
 * Reconcile on connect/reconnect using the SESSIONS_RECONCILE server payload.
 *
 * For each session ID reported by the server as "open" that is NOT yet tracked
 * by the store (mode === 'idle'): marks it fetching_initial and emits
 * LOAD_TAB_MESSAGES via the wire. This ensures URL deep-link sessions and
 * cross-device sessions are loaded even if they are not in the frontend's
 * saved-tabs list (Bug #2 from the 2026-05-30 session-load forensic).
 *
 * Sessions already in fetching_initial / loaded / fetching_older are skipped —
 * the startup LOAD_TAB_MESSAGES loop already handles them.
 */
export function applyReconcile(openSessionIds: string[]): void {
  const wire = _wire;
  if (!wire || !openSessionIds.length) return;

  const currentSessions = useSessionLoadStore.getState().sessions;
  for (const sid of openSessionIds) {
    const current = currentSessions.get(sid);
    if (!current || current.mode === 'idle' || current.mode === 'error') {
      startFetchingInitial(sid);
      wire.socketEmit(WsEvent.LOAD_TAB_MESSAGES, { sessionId: sid });
    }
  }
}

/**
 * Drop ALL per-session load state (housekeeping, not an FSM transition — no
 * TRANSITIONS row). Sole legitimate use today is spec isolation between tests
 * (SE-SL-SOLE-OWNER forbids external `useSessionLoadStore.setState` callers,
 * specs included); production code has no full-reset intent — reconnects go
 * through applyReconcile, session removal through server-driven events.
 */
export function resetAllSessions(): void {
  for (const sid of fetchTimers.keys()) clearFetchTimer(sid);
  useSessionLoadStore.setState({ sessions: new Map<string, SessionLoadState>() });
}

// ─── Non-hook accessors ───────────────────────────────────────────────────────

/**
 * Non-hook variant. Use from callsites that already have store state
 * (event handlers, effect bodies that already subscribed). Returns the
 * empty default if sessionId is null or session is unknown.
 */
export function getSessionMessages(
  state: SessionLoadStoreShape,
  sessionId: string | null,
): ChatMessage[] {
  if (!sessionId) return [];
  return state.sessions.get(sessionId)?.messages ?? [];
}

// ─── Hook selectors — the ONLY supported way to read per-session load state ──

/**
 * React hook — subscribes to the messages array for a single session.
 * Re-renders only when the messages reference for `sessionId` changes,
 * not on unrelated session mutations. Pass `null` for sessions that
 * don't exist yet — returns `[]` without subscribing to anything
 * destabilising.
 */
export function useSessionMessages(sessionId: string | null): ChatMessage[] {
  return useSessionLoadStore(
    useShallow((state) => {
      if (!sessionId) return [];
      return state.sessions.get(sessionId)?.messages ?? [];
    }),
  );
}

/**
 * React hook — subscribes to the hasMore flag for a single session.
 * Drives the "Load older messages" scroll-up affordance in ChatArea
 * (Phase 3). Defaults to `true` for unknown sessions — optimistic, same
 * rationale as PR #1236.
 */
export function useSessionHasMore(sessionId: string | null): boolean {
  return useSessionLoadStore(
    useShallow((state) => {
      if (!sessionId) return false;
      return state.sessions.get(sessionId)?.hasMore ?? true;
    }),
  );
}

/**
 * React hook — subscribes to the FSM mode for a single session. Use to
 * render loading skeletons (Phase 4), retry buttons (error mode), or
 * to gate user actions on `loaded` only.
 */
export function useSessionLoadState(sessionId: string | null): SessionLoadMode {
  return useSessionLoadStore(
    useShallow((state) => {
      if (!sessionId) return 'idle';
      return state.sessions.get(sessionId)?.mode ?? 'idle';
    }),
  );
}

/**
 * React hook — convenience over `useSessionLoadState` for "is anything
 * happening for this session" UI gates. True iff mode is fetching_initial
 * or fetching_older.
 */
export function useIsSessionLoading(sessionId: string | null): boolean {
  const mode = useSessionLoadState(sessionId);
  return mode === 'fetching_initial' || mode === 'fetching_older';
}
